import * as React from "react";
import * as DropdownMenuPrimitives from "@radix-ui/react-dropdown-menu";
import {
  RiArrowRightSLine,
  RiCheckboxBlankCircleLine,
  RiCheckLine,
  RiRadioButtonFill,
} from "@remixicon/react";

import { cx } from "../utils";

const interactiveMenuItemClasses = [
  "hover:bg-white/[.08] hover:text-highlight hover:dark:text-highlight",
  "focus-visible:bg-white/[.08] focus-visible:text-highlight focus-visible:dark:text-highlight",
  "data-[highlighted]:bg-white/[.08] data-[highlighted]:text-highlight data-[highlighted]:dark:text-highlight",
  "[&:hover_*]:!text-highlight [&[data-highlighted]_*]:!text-highlight",
];

const DropdownMenu = DropdownMenuPrimitives.Root;
DropdownMenu.displayName = "DropdownMenu";

const DropdownMenuTrigger = DropdownMenuPrimitives.Trigger;
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuGroup = DropdownMenuPrimitives.Group;
DropdownMenuGroup.displayName = "DropdownMenuGroup";

const DropdownMenuSubMenu = DropdownMenuPrimitives.Sub;
DropdownMenuSubMenu.displayName = "DropdownMenuSubMenu";

const DropdownMenuRadioGroup = DropdownMenuPrimitives.RadioGroup;
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";

const DropdownMenuSubMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.SubTrigger>,
  Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.SubTrigger>,
    "asChild"
  >
>(({ className, children, ...props }, forwardedRef) => (
  <DropdownMenuPrimitives.SubTrigger
    ref={forwardedRef}
    className={cx(
      // base
      "relative flex cursor-default select-none items-center rounded py-1.5 pl-2 pr-1 outline-none transition-colors data-[state=checked]:font-semibold sm:text-sm",
      // text color
      "text-gray-900 dark:text-gray-50",
      // disabled
      "data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600",
      interactiveMenuItemClasses,
      "data-[state=open]:bg-white/[.08] data-[state=open]:text-highlight data-[state=open]:dark:text-highlight",
      className,
    )}
    {...props}
  >
    {children}
    <RiArrowRightSLine className="ml-auto size-4 shrink-0" aria-hidden="true" />
  </DropdownMenuPrimitives.SubTrigger>
));
DropdownMenuSubMenuTrigger.displayName = "DropdownMenuSubMenuTrigger";

const DropdownMenuSubMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.SubContent>
>(({ className, collisionPadding = 8, ...props }, forwardedRef) => (
  <DropdownMenuPrimitives.Portal>
    <DropdownMenuPrimitives.SubContent
      ref={forwardedRef}
      collisionPadding={collisionPadding}
      className={cx(
        // base
        "relative z-50 overflow-hidden rounded-md border p-1 shadow-xl shadow-black/[2.5%]",
        // widths
        "min-w-32",
        // heights
        "max-h-[var(--radix-popper-available-height)]",
        // background color
        "bg-secondaryBg",
        // text color
        "text-primary",
        // border color
        "border-zinc-800",
        // transition
        "will-change-[transform,opacity]",
        // "data-[state=open]:animate-slideDownAndFade",
        "data-[state=closed]:animate-hide",
        "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitives.Portal>
));
DropdownMenuSubMenuContent.displayName = "DropdownMenuSubMenuContent";

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Content>
>(
  (
    {
      className,
      sideOffset = 8,
      collisionPadding = 8,
      align = "center",
      loop = true,
      ...props
    },
    forwardedRef,
  ) => (
    <DropdownMenuPrimitives.Portal>
      <DropdownMenuPrimitives.Content
        ref={forwardedRef}
        className={cx(
          // base
          "relative z-50 overflow-hidden rounded-md border p-1 shadow-xl shadow-black/[2.5%]",
          // widths
          "min-w-48",
          // heights
          "max-h-[var(--radix-popper-available-height)]",
          "bg-secondaryBg",
          // text color
          "text-primary",
          // border color
          "border-zinc-800",
          // transition
          "will-change-[transform,opacity]",
          "data-[state=closed]:animate-hide",
          "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade",
          className,
        )}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        loop={loop}
        {...props}
      />
    </DropdownMenuPrimitives.Portal>
  ),
);
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Item>,
  Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Item>,
    "asChild"
  > & {
    shortcut?: string;
    hint?: string;
  }
>(({ className, shortcut, hint, children, ...props }, forwardedRef) => (
  <DropdownMenuPrimitives.Item
    ref={forwardedRef}
    className={cx(
      // base
      "group/DropdownMenuItem relative flex cursor-pointer select-none items-center rounded py-1.5 pl-2 pr-1 outline-none transition-colors data-[state=checked]:font-semibold sm:text-sm",
      // text color
      "text-gray-900 dark:text-gray-50",
      // disabled
      "data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600",
      interactiveMenuItemClasses,
      className,
    )}
    tremor-id="tremor-raw"
    {...props}
  >
    {children}
    {hint && (
      <span
        className={cx(
          "ml-auto pl-2 text-sm text-gray-400 dark:text-gray-600 group-hover/DropdownMenuItem:text-highlight group-data-[highlighted]/DropdownMenuItem:text-highlight",
        )}
      >
        {hint}
      </span>
    )}
    {shortcut && (
      <span
        className={cx(
          "ml-auto pl-2 text-sm text-gray-400 dark:text-gray-600 group-hover/DropdownMenuItem:text-highlight group-data-[highlighted]/DropdownMenuItem:text-highlight",
        )}
      >
        {shortcut}
      </span>
    )}
  </DropdownMenuPrimitives.Item>
));
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.CheckboxItem>,
  Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.CheckboxItem>,
    "asChild"
  > & {
    shortcut?: string;
    hint?: string;
    indicator?: "check" | "switch";
  }
>(
  (
    {
      className,
      hint,
      shortcut,
      children,
      checked,
      indicator = "check",
      ...props
    },
    forwardedRef,
  ) => (
    <DropdownMenuPrimitives.CheckboxItem
      ref={forwardedRef}
      className={cx(
        // base
        "group/DropdownMenuCheckboxItem relative flex cursor-pointer select-none items-center gap-x-2 rounded py-1.5 pr-1 outline-none transition-colors data-[state=checked]:font-semibold sm:text-sm",
        indicator === "switch" ? "pl-2" : "pl-8",
        // text color
        "text-gray-900 dark:text-gray-50",
        // disabled
        "data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600",
        interactiveMenuItemClasses,
        className,
      )}
      checked={checked}
      {...props}
    >
      {indicator === "check" && (
        <span className="absolute left-2 flex size-4 items-center justify-center">
          <DropdownMenuPrimitives.ItemIndicator>
            <RiCheckLine
              aria-hidden="true"
              className="size-full shrink-0 text-gray-800 dark:text-gray-200"
            />
          </DropdownMenuPrimitives.ItemIndicator>
        </span>
      )}
      {children}
      {hint && (
        <span
          className={cx(
            "ml-auto text-sm font-normal text-gray-500 dark:text-gray-400 group-hover/DropdownMenuCheckboxItem:text-highlight group-data-[highlighted]/DropdownMenuCheckboxItem:text-highlight",
          )}
        >
          {hint}
        </span>
      )}
      {shortcut && (
        <span
          className={cx(
            "ml-auto text-sm font-normal tracking-widest text-gray-400 dark:border-gray-800 dark:text-gray-600 group-hover/DropdownMenuCheckboxItem:text-highlight group-data-[highlighted]/DropdownMenuCheckboxItem:text-highlight",
          )}
        >
          {shortcut}
        </span>
      )}
      {indicator === "switch" && (
        <span
          aria-hidden="true"
          className={cx(
            "group relative isolate inline-flex h-4 w-7 shrink-0 items-center rounded-full bg-secondaryBg p-0.5 shadow-inner ring-1 ring-inset ring-zinc-800 transition-all",
            "data-[state=checked]:bg-highlight",
            "group-hover/DropdownMenuCheckboxItem:bg-black group-data-[highlighted]/DropdownMenuCheckboxItem:bg-black",
            "data-[disabled]:bg-gray-100 data-[disabled]:ring-gray-300 dark:data-[disabled]:bg-gray-800 dark:data-[disabled]:ring-gray-700",
            "data-[disabled]:data-[state=checked]:bg-highlightDarker data-[disabled]:data-[state=checked]:ring-gray-300 dark:data-[disabled]:data-[state=checked]:bg-highlightDarker dark:data-[disabled]:data-[state=checked]:ring-gray-900",
            hint ? "ml-2" : "ml-auto",
          )}
          data-disabled={props.disabled ? "" : undefined}
          data-slot="switch-indicator"
          data-state={checked === true ? "checked" : "unchecked"}
        >
          <span
            className={cx(
              "pointer-events-none relative inline-block h-3 w-3 transform appearance-none rounded-full border-none bg-white shadow-lg outline-none transition-all duration-150 ease-in-out dark:bg-gray-50",
              "group-data-[disabled]:bg-gray-50 group-data-[disabled]:shadow-none group-data-[disabled]:dark:bg-gray-500",
              checked === true ? "translate-x-3" : "translate-x-0",
            )}
          />
        </span>
      )}
    </DropdownMenuPrimitives.CheckboxItem>
  ),
);
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.RadioItem>,
  Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.RadioItem>,
    "asChild"
  > & {
    shortcut?: string;
    hint?: string;
    indicator?: "radio" | "check";
  }
>(
  (
    { className, hint, shortcut, indicator = "radio", children, ...props },
    forwardedRef,
  ) => (
    <DropdownMenuPrimitives.RadioItem
      ref={forwardedRef}
      className={cx(
        // base
        "group/DropdownMenuRadioItem relative flex cursor-pointer select-none items-center gap-x-2 rounded py-1.5 pl-8 pr-1 outline-none transition-colors data-[state=checked]:font-semibold sm:text-sm",
        // text color
        "text-gray-900 dark:text-gray-50",
        // disabled
        "data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600",
        interactiveMenuItemClasses,
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        {indicator === "check" ? (
          <RiCheckLine
            aria-hidden="true"
            className="size-full shrink-0 text-highlight group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden"
          />
        ) : (
          <>
            <RiRadioButtonFill
              aria-hidden="true"
              className="size-full shrink-0 text-blue-500 group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden dark:text-blue-500"
            />
            <RiCheckboxBlankCircleLine
              aria-hidden="true"
              className="size-full shrink-0 text-gray-300 group-data-[state=unchecked]/DropdownMenuRadioItem:flex group-data-[state=checked]/DropdownMenuRadioItem:hidden dark:text-gray-700"
            />
          </>
        )}
      </span>
      {children}
      {hint && (
        <span
          className={cx(
            "ml-auto text-sm font-normal text-gray-400 dark:text-gray-600 group-hover/DropdownMenuRadioItem:text-highlight group-data-[highlighted]/DropdownMenuRadioItem:text-highlight",
          )}
        >
          {hint}
        </span>
      )}
      {shortcut && (
        <span
          className={cx(
            "ml-auto text-sm font-normal tracking-widest text-gray-400 dark:border-gray-800 dark:text-gray-600 group-hover/DropdownMenuRadioItem:text-highlight group-data-[highlighted]/DropdownMenuRadioItem:text-highlight",
          )}
        >
          {shortcut}
        </span>
      )}
    </DropdownMenuPrimitives.RadioItem>
  ),
);
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Label>
>(({ className, ...props }, forwardedRef) => (
  <DropdownMenuPrimitives.Label
    ref={forwardedRef}
    className={cx(
      // base
      "px-2 py-2 text-xs font-medium tracking-wide",
      // text color
      "dark:text-[#8f8f8f]",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Separator>
>(({ className, ...props }, forwardedRef) => (
  <DropdownMenuPrimitives.Separator
    ref={forwardedRef}
    className={cx("-mx-1 my-1 h-px border-t border-zinc-800", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuIconWrapper = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <div
      className={cx(
        // text color
        "text-gray-600 dark:text-gray-400",
        "group-hover/DropdownMenuItem:text-highlight group-data-[highlighted]/DropdownMenuItem:text-highlight",
        // disabled
        "group-data-[disabled]/DropdownMenuItem:text-gray-400 group-data-[disabled]/DropdownMenuItem:dark:text-gray-700",
        className,
      )}
      {...props}
    />
  );
};
DropdownMenuIconWrapper.displayName = "DropdownMenuIconWrapper";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSubMenuTrigger,
  DropdownMenuSubMenu,
  DropdownMenuSubMenuContent,
  DropdownMenuGroup,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
  DropdownMenuIconWrapper,
  DropdownMenuLabel,
  DropdownMenuSeparator,
};
